import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X, LogOut } from "lucide-react";

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleLogout = async () => {
        await logout();
        navigate("/");
    };

    const linkBase = "text-sm font-medium tracking-wide transition-colors";
    const linkInactive = "text-tmc-inkSoft hover:text-tmc-terracotta";
    const linkActive = "text-tmc-terracotta";

    return (
        <header className="sticky top-0 z-40 bg-white/75 backdrop-blur-xl border-b border-tmc-line">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
                    <span className="tmc-logo-mark w-10 h-10">
                        <img src="/tmc-logo.png" alt="TMC" className="w-full h-full object-contain" />
                    </span>
                    <div className="leading-tight">
                        <div className="font-serif text-lg text-tmc-ink">Moments</div>
                        <div className="label-overline -mt-1" style={{ fontSize: "0.55rem" }}>
                            That Move Us
                        </div>
                    </div>
                    <span className="hidden sm:block h-10 w-px bg-tmc-line mx-2" aria-hidden="true" />
                    <img
                        src="/tmc-horizontal.png"
                        alt="Together Making Changes"
                        className="hidden sm:block h-14 w-auto object-contain"
                        data-testid="tmc-horizontal-logo"
                    />
                </Link>

                <nav className="hidden md:flex items-center gap-8">
                    <NavLink
                        to="/submit"
                        className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
                        data-testid="nav-submit"
                    >
                        Share a Moment
                    </NavLink>
                    {user && (
                        <NavLink
                            to="/vote"
                            className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
                            data-testid="nav-vote"
                        >
                            Vote
                        </NavLink>
                    )}
                    {user?.role === "admin" && (
                        <>
                            <NavLink
                                to="/admin"
                                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
                                data-testid="nav-admin"
                            >
                                Submissions
                            </NavLink>
                            <NavLink
                                to="/admin/voting"
                                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
                                data-testid="nav-admin-voting"
                            >
                                Voting Controls
                            </NavLink>
                            <NavLink
                                to="/admin/team"
                                className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`}
                                data-testid="nav-admin-team"
                            >
                                Team
                            </NavLink>
                        </>
                    )}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    {user ? (
                        <>
                            <span className="text-sm text-tmc-muted" data-testid="navbar-user-email">
                                {user.email}
                            </span>
                            <button
                                onClick={handleLogout}
                                className="text-sm font-medium text-tmc-inkSoft hover:text-tmc-terracotta inline-flex items-center gap-1.5"
                                data-testid="logout-btn"
                            >
                                <LogOut className="w-4 h-4" /> Sign out
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/login" className="text-sm font-medium text-tmc-inkSoft hover:text-tmc-terracotta" data-testid="nav-login">
                                Sign in with Microsoft
                            </Link>
                            <Link to="/submit" className="tmc-btn-primary text-sm py-2 px-5" data-testid="nav-cta-submit">
                                Share a Moment
                            </Link>
                        </>
                    )}
                </div>

                <button
                    className="md:hidden p-2 text-tmc-ink"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="mobile-menu-btn"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t border-tmc-line bg-white" data-testid="mobile-menu">
                    <nav className="px-4 py-4 flex flex-col gap-3">
                        <Link to="/submit" onClick={() => setOpen(false)} className="text-tmc-ink py-2">
                            Share a Moment
                        </Link>
                        {user && (
                            <Link to="/vote" onClick={() => setOpen(false)} className="text-tmc-ink py-2">
                                Vote
                            </Link>
                        )}
                        {user?.role === "admin" && (
                            <>
                                <Link to="/admin" onClick={() => setOpen(false)} className="text-tmc-ink py-2">
                                    Submissions
                                </Link>
                                <Link to="/admin/voting" onClick={() => setOpen(false)} className="text-tmc-ink py-2">
                                    Voting Controls
                                </Link>
                                <Link to="/admin/team" onClick={() => setOpen(false)} className="text-tmc-ink py-2">
                                    Team
                                </Link>
                            </>
                        )}
                        <div className="border-t border-tmc-line pt-3 mt-1">
                            {user ? (
                                <button
                                    onClick={() => {
                                        setOpen(false);
                                        handleLogout();
                                    }}
                                    className="text-tmc-terracotta font-medium"
                                    data-testid="mobile-logout-btn"
                                >
                                    Sign out
                                </button>
                            ) : (
                                <Link to="/login" onClick={() => setOpen(false)} className="text-tmc-terracotta font-medium">
                                    Sign in with Microsoft
                                </Link>
                            )}
                        </div>
                    </nav>
                </div>
            )}
        </header>
    );
}
